import { useState, useRef } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { TierBadge, ScoreChip } from "@/components/TierBadge";
import { Sparkles, Save, Upload, Loader2, ClipboardPaste, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const FIELD_LABELS = {
  name: "Name *", company: "Company", self_described_title: "Self-described title",
  formal_title: "Formal title", headline: "Headline", follower_count: "Follower count",
  location: "Location", experience_years: "Experience (yrs)", current_role_start_year: "Role start year",
  industry: "Industry", education: "Education", linkedin_url: "LinkedIn URL",
  phone: "Phone", email: "Email", verification_badge: "Verified badge",
};

const EMPTY = {
  name: "", headline: "", company: "", self_described_title: "", formal_title: "",
  follower_count: 0, location: "", experience_years: 0, current_role_start_year: "",
  education: "", industry: "", verification_badge: false, open_to_work: false,
  linkedin_url: "", phone: "", email: "", source: "Manual Paste", raw_text: "",
};

const FIELDS = [
  ["name", "Name", "text"], ["company", "Company", "text"],
  ["self_described_title", "Self-described title", "text"], ["headline", "Headline", "text"],
  ["follower_count", "Follower count", "number"], ["location", "Location", "text"],
  ["experience_years", "Experience (yrs)", "number"], ["current_role_start_year", "Current role start yr", "number"],
  ["industry", "Industry (context only)", "text"], ["education", "Education", "text"],
  ["linkedin_url", "LinkedIn URL", "text"], ["phone", "Phone", "text"], ["email", "Email", "text"],
];

export default function Intake() {
  const [raw, setRaw] = useState("");
  const [form, setForm] = useState({ ...EMPTY });
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapFile, setMapFile] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const parse = async () => {
    if (!raw.trim()) { toast.error("Paste a profile first"); return; }
    setParsing(true);
    try {
      const { data } = await api.post("/leads/parse", { text: raw });
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null && v !== undefined)), raw_text: raw }));
      toast.success("Parsed — review & fix any missed fields");
    } catch (e) { toast.error(errText(e)); } finally { setParsing(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, follower_count: Number(form.follower_count) || 0, experience_years: Number(form.experience_years) || 0, current_role_start_year: form.current_role_start_year ? Number(form.current_role_start_year) : null, raw_text: raw };
      const { data } = await api.post("/leads", payload);
      setPreview(data);
      toast.success(`Lead saved — scored ${data.fit_score}/100 (${data.tier})`);
      setForm({ ...EMPTY }); setRaw("");
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/leads/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMapFile(file);
      setMapData(data);
      setMapping(data.suggested_mapping || {});
      setMapOpen(true);
    } catch (e2) { toast.error(errText(e2)); }
    e.target.value = "";
  };

  const runImport = async () => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", mapFile);
    fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} leads (${data.skipped} skipped)`);
      setMapOpen(false); setMapFile(null); setMapData(null);
    } catch (e2) { toast.error(errText(e2)); } finally { setImporting(false); }
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Lead Intake</h1>
          <p className="text-xs text-zinc-500 font-mono">Paste a LinkedIn profile · parse · score. No API — manual paste & CSV only.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={importFile} className="hidden" data-testid="csv-input" />
          <button data-testid="csv-import-btn" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 bg-white border border-zinc-400 px-4 py-2 text-sm font-medium hover:bg-zinc-100 transition-colors">
            <Upload className="h-4 w-4" /> Import CSV / Excel
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-zinc-300 border border-zinc-300">
        {/* Paste side */}
        <div className="bg-white p-5">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-900 mb-1 flex items-center gap-1.5">
            <ClipboardPaste className="h-3.5 w-3.5" /> Raw LinkedIn profile
          </label>
          <textarea data-testid="lead-intake-paste-area" value={raw} onChange={(e) => setRaw(e.target.value)}
            placeholder={"Paste name, headline, About, experience, followers…\n\nExample:\nPriya Sharma\nFounder @ NovaLabs | Building the future of SaaS 🚀 | TEDx Speaker\n12,400 followers"}
            className="w-full h-72 border border-zinc-400 p-3 text-sm font-mono resize-none focus:border-klein focus:outline-none focus:ring-1 focus:ring-klein" />
          <button data-testid="parse-btn" onClick={parse} disabled={parsing}
            className="mt-3 w-full bg-klein hover:bg-klein-hover text-white font-medium py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Parse profile
          </button>
        </div>

        {/* Form side */}
        <div className="bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-900 mb-3">Review fields (fix anything the parser missed)</div>
          <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
            {FIELDS.map(([k, label, type]) => (
              <div key={k} className={k === "headline" || k === "linkedin_url" ? "col-span-2" : ""}>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5 block">{label}</label>
                <input data-testid={`field-${k}`} type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)}
                  className="w-full border border-zinc-300 px-2 py-1.5 text-sm focus:border-klein focus:outline-none" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" checked={!!form.verification_badge} onChange={(e) => set("verification_badge", e.target.checked)} className="accent-klein" data-testid="field-verified" /> Verified badge</label>
            <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" checked={!!form.open_to_work} onChange={(e) => set("open_to_work", e.target.checked)} className="accent-klein" data-testid="field-otw" /> Open to work</label>
          </div>
          <button data-testid="save-lead-btn" onClick={save} disabled={saving}
            className="mt-4 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 flex items-center justify-center gap-2 transition-colors hard-shadow-sm disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; score lead
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-5 border-2 border-black bg-white p-4 rise" data-testid="intake-preview">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-head font-bold text-lg">{preview.name} scored!</div>
              <div className="text-xs text-zinc-500">{preview.next_action}</div>
            </div>
            <div className="flex items-center gap-3">
              <ScoreChip score={preview.fit_score} /><TierBadge tier={preview.tier} />
            </div>
          </div>
        </div>
      )}

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-3xl rounded-none border-2 border-black" data-testid="csv-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Map columns · {mapData?.total} rows detected</DialogTitle>
          </DialogHeader>
          {mapData && (
            <div className="space-y-4">
              <p className="text-xs text-zinc-500">Match each Sales Navigator column to a lead field. We pre-filled our best guesses — fix any that look wrong. Only <b>Name</b> is required.</p>

              <div className="border border-zinc-300 overflow-x-auto max-h-40">
                <table className="text-[11px] font-mono w-full">
                  <thead className="bg-zinc-900 text-white sticky top-0">
                    <tr>{mapData.columns.map((c) => <th key={c} className="px-2 py-1 text-left whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {mapData.rows.map((r, i) => (
                      <tr key={i} className="border-b border-zinc-200">
                        {mapData.columns.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap max-w-[120px] truncate">{r[c]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 max-h-64 overflow-y-auto pr-1">
                {mapData.target_fields.map((tf) => (
                  <div key={tf} className="flex items-center gap-2">
                    <span className="text-xs w-32 shrink-0 font-medium">{FIELD_LABELS[tf] || tf}</span>
                    <select
                      data-testid={`map-${tf}`}
                      value={mapping[tf] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [tf]: e.target.value }))}
                      className="flex-1 border border-zinc-400 text-xs px-2 py-1.5 focus:border-klein focus:outline-none"
                    >
                      <option value="">— skip —</option>
                      {mapData.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setMapOpen(false)} className="px-4 py-2 text-sm border border-zinc-400 hover:bg-zinc-100 transition-colors">Cancel</button>
            <button data-testid="confirm-import-btn" onClick={runImport} disabled={importing || !mapping.name}
              className="px-4 py-2 text-sm bg-klein hover:bg-klein-hover text-white font-medium flex items-center gap-2 transition-colors disabled:opacity-60">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Import {mapData?.total} leads
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
